import { planItemCompletesStage } from './reportPlan';
/**
 * stageBoards — the TWO-STAGE view of an analysis plan, shared by the
 * Analyses page and Path to GO › Analyse (owner-approved redesign,
 * 2026-09-06): one board per stage, one-line rows, the single next step.
 *
 * Pure projection over `ReportPlanItem[]`. Lanes, priorities and the next
 * step keep the semantics both surfaces already agreed on:
 *   - `analysisLane` decides run-now / available-later / optional; a run-now
 *     item that `satisfiesGo` is CURRENT.
 *   - Priority numbers count the STARTABLE run-now rows in plan order (the
 *     same numbering GenerateStage and the Findings matrix rendered).
 *   - The next step follows the pipeline: the first startable Stage 1 row →
 *     the promoted-scenario step once screening is complete → the first
 *     startable Stage 2 row → running work → the decision.
 * Nothing here decides admission: the server chokepoint stays the only test.
 */
import {
  DECISION_STAGE_LABEL,
  REPORT_TYPE_REGISTRY,
  reportTypeStage,
  type DecisionStage,
  type ReportTypeId,
} from '../../config/reportTypes';
import type { PathAction, ReportPlanItem } from './reportPlan';
import type { ScreeningOutcome } from '../../types/portfolioComposite';
import type { DecisionStagesRollup } from '../executiveDecisionRollup';
import { analysisLane, laterLabel, type AnalysisLane } from './reportPlanLanes';

export type StageRowLane = AnalysisLane | 'current';

export interface StageBoardRow {
  item: ReportPlanItem;
  type: ReportTypeId;
  label: string;
  stage: DecisionStage | undefined;
  lane: StageRowLane;
  /** Run-now, not current, not running — work the reader can start. */
  startable: boolean;
  running: boolean;
  /** Safe calculation input exists, whether current or saved. */
  used: boolean;
  /** The calculation input is current (a strict subset of `used`). */
  current: boolean;
  /** 1-based rank among startable rows, plan order. */
  priority?: number;
  /** The plan's one-line reason for the available-later lane. */
  laterReason?: string;
  /** A readable analysis or report exists for the row. */
  hasRecord: boolean;
  /**
   * Waits on nothing but the promoted scenario, so it is listed under the
   * scenario step ("Then · …") instead of as a row of its own — one step, not
   * three (owner, 2026-09-06). Never set on a row with a record: an existing
   * report keeps its own row and Open.
   */
  folded?: boolean;
}

export type StageBoardState =
  | 'complete'
  | 'open'
  | 'running'
  | 'scenario'
  | 'after-screening'
  | 'nothing-required';

export interface StageScenarioStep {
  /** Stage 2 analyses wait for a promoted generation scenario. */
  needed: boolean;
  /** …and early screening is not complete yet, so the scenario comes after it. */
  afterScreening: boolean;
  /** The plan's own route to the scenario (Generation & Impact), when it names one. */
  action: PathAction | null;
  /**
   * Rows folded into this step — they run against the scenario once it is
   * promoted. Only rows WITHOUT a record: a locked analysis that already has a
   * report (a saved result, a draft, a legacy report without a revision, a
   * withheld or metadata-only one) keeps its own row with Open.
   */
  dependants: StageBoardRow[];
  /** The promoted scenario changed under the analyses: re-promote, not promote. */
  stale: boolean;
}

export interface StageBoardModel {
  stage: DecisionStage;
  label: string;
  /** Required + intent-relevant rows of this stage (every lane but optional). */
  rows: StageBoardRow[];
  requiredTotal: number;
  /** Required rows backed by either current or saved-safe calculation inputs. */
  requiredUsed: number;
  requiredCurrent: number;
  /**
   * Required rows with a saved record (`hasRecord`), current or not — the
   * dashboard's "· K saved" (DecisionPipelineBoard). Display only: it never
   * feeds `requiredCurrent`, `state` or the next step.
   */
  requiredSaved: number;
  state: StageBoardState;
  scenario?: StageScenarioStep;
  /** Stage 1 only: the screening result once every screening report is present. */
  outcome?: ScreeningOutcome;
  /** Stage 1 only: the reports behind a `concerns` / `declined` outcome. */
  concernTypes?: ReportTypeId[];
  /** Stage 1 only: the screening score (0–100) and its coverage — never a band. */
  score?: number;
  scoreCoverage?: number;
  /** Current only when both the canonical plan and rollup qualify every required report. */
  scoreBasis?: 'current' | 'saved' | 'provisional';
}

export type StageNextStep =
  | { kind: 'analysis'; row: StageBoardRow; action: PathAction | null }
  | { kind: 'scenario'; action: PathAction | null }
  | { kind: 'running'; row: StageBoardRow }
  | { kind: 'decision' }
  | { kind: 'none' };

export interface StageBoardsModel {
  screening: StageBoardModel;
  validation: StageBoardModel;
  /**
   * Lane `optional` rows — the Analyses tab's "Additional analyses": advisory
   * checks, unscored briefs and Public Sentiment. Never on a stage board.
   */
  optional: StageBoardRow[];
  runNow: StageBoardRow[];
  inFlight: StageBoardRow[];
  current: StageBoardRow[];
  later: StageBoardRow[];
  next: StageNextStep;
  /**
   * Startable rows split by the verb of their own action
   * (`startableWorkKind`): `toRefreshCount` saved rows need a refresh,
   * `toRunCount` rows have never run, `needsAttentionCount` saved rows need a
   * review, retry or written report (held, failed, preliminary, preview,
   * coverage). Together they are `runNow.length`. Surfaces word them with
   * `startableWorkLabel`.
   */
  toRefreshCount: number;
  toRunCount: number;
  needsAttentionCount: number;
}

function reportTypeLabel(type: ReportTypeId): string {
  return REPORT_TYPE_REGISTRY[type]?.label ?? type;
}

function isRunning(item: ReportPlanItem): boolean {
  return item.lifecycle === 'queued' || item.lifecycle === 'generating';
}

/**
 * Analyses whose ONLY prerequisite is the promoted scenario: they fold into the
 * scenario step. Financial and Supply Chain keep their own rows — their inputs
 * (model, equipment basis) can be prepared while the scenario is solved.
 * Environmental (Stage 2 since 2026-09-30) waits on the scenario alone, so it
 * folds with Generation & Impact and Test Fit.
 */
const SCENARIO_DEPENDANTS: ReadonlySet<ReportTypeId> = new Set<ReportTypeId>(['power-availability', 'test-fit', 'environmental']);

/**
 * The scenario step's own route when no gated row offers one — the action the
 * Generation & Impact plan item carries while its scenario is missing or stale.
 * The New Report wizard's stage-lock advisory routes here too.
 */
export const SCENARIO_ROUTE: PathAction = { kind: 'generation-impact', label: 'Configure generation scenario', focus: 'scenario' };

/** The plan item names the promoted generation scenario as what it waits for. */
export function isScenarioGate(item: ReportPlanItem): boolean {
  const code = item.eligibility?.reasonCode;
  if (code === 'stage-locked' || code === 'generation-scenario-missing' || code === 'generation-scenario-stale') return true;
  const via = item.eligibility?.nextAction ?? item.primaryAction;
  return via?.kind === 'generation-impact' && via.focus !== 'layout';
}

function toRow(item: ReportPlanItem, priority: number | undefined): StageBoardRow {
  const baseLane = analysisLane(item);
  const used = planItemCompletesStage(item);
  const current = item.calculationStatus != null
    ? item.calculationStatus === 'current'
    : used && item.lifecycle === 'current' && !item.stale;
  const lane: StageRowLane = baseLane === 'run-now' && current ? 'current' : baseLane;
  const running = isRunning(item);
  return {
    item,
    type: item.type,
    label: reportTypeLabel(item.type),
    stage: item.stage ?? reportTypeStage(item.type) ?? undefined,
    lane,
    startable: !used && lane === 'run-now' && !running,
    running,
    used,
    current,
    ...(priority != null ? { priority } : {}),
    ...(lane === 'available-later' ? { laterReason: laterLabel(item) } : {}),
    hasRecord: Boolean(item.analysisId || item.reportId || item.calculationReportId),
  };
}

function boardState(rows: StageBoardRow[]): StageBoardState {
  if (rows.length === 0) return 'nothing-required';
  if (rows.every((row) => row.used)) return 'complete';
  if (rows.some((row) => row.running) && !rows.some((row) => row.startable)) return 'running';
  return 'open';
}

export function buildStageBoards(plan: readonly ReportPlanItem[]): StageBoardsModel {
  let rank = 0;
  const rows = plan.map((item) => {
    const row = toRow(item, undefined);
    if (row.startable) row.priority = ++rank;
    return row;
  });
  const staged = rows.filter((row) => row.lane !== 'optional');
  const optional = rows.filter((row) => row.lane === 'optional');
  const ofStage = (stage: DecisionStage) => staged.filter((row) => row.stage === stage);
  const screeningRows = ofStage(1);
  const validationRows = ofStage(2);

  // Counts, completion and the decision step read the REQUIRED rows only —
  // an intent-relevant analysis is listed and startable, but it neither
  // holds the decision nor counts toward "n of m current".
  const requiredOf = (list: StageBoardRow[]) => list.filter((row) => row.item.role === 'required');
  const screeningRequired = requiredOf(screeningRows);
  const screening: StageBoardModel = {
    stage: 1,
    label: DECISION_STAGE_LABEL[1],
    rows: screeningRows,
    requiredTotal: screeningRequired.length,
    requiredUsed: screeningRequired.filter((row) => row.used).length,
    requiredCurrent: screeningRequired.filter((row) => row.current).length,
    requiredSaved: screeningRequired.filter((row) => row.hasRecord).length,
    state: boardState(screeningRequired),
  };
  const screeningComplete = screening.state === 'complete' || screening.state === 'nothing-required';

  const gated = validationRows.filter((row) => !row.used && isScenarioGate(row.item));
  const scenarioItem = gated[0];
  // ONE step, not three: the Generation & Impact report, the Test Fit and the
  // Environmental assessment wait on nothing but the scenario, so they are
  // listed under the step as "Then · …" rather than as rows of their own. They
  // stay in `rows` and `later` — counts and "waiting on inputs" are unchanged.
  const scenarioDependants = gated.filter((row) => SCENARIO_DEPENDANTS.has(row.type));
  // Only a dependant with NO record folds. A locked analysis that already has
  // a report the plan cannot use (an Environmental run while it was Stage 1
  // and saved as a draft, a legacy report without a revision, a withheld or
  // metadata-only one) keeps its own row so the report stays openable; the
  // Analyses tab renders only labels under the step.
  const dependants = scenarioDependants.filter((row) => !row.hasRecord);
  for (const row of dependants) row.folded = true;
  // The step's button is the plan's own route to the scenario. Environmental
  // (required for every site) can precede Generation & Impact in plan order,
  // and its locked action is its own report setup — which the server refuses
  // until the scenario exists — so a dependant that routes to the scenario
  // workspace leads when there is one (folded or not: the route is the same).
  const routesToScenario = (row: StageBoardRow): boolean =>
    (row.item.eligibility?.nextAction ?? row.item.primaryAction)?.kind === 'generation-impact';
  const lead = scenarioDependants.find(routesToScenario) ?? scenarioDependants[0] ?? scenarioItem;
  const leadAction = lead ? (lead.item.eligibility?.nextAction ?? lead.item.primaryAction ?? null) : null;
  // No gated row routes to the scenario (Generation & Impact already used while
  // the scenario is missing or stale): a stage-locked analysis's own setup is
  // still refused until a scenario is promoted, so the step routes to the
  // scenario workspace itself rather than to a launch the server refuses.
  const scenarioAction = leadAction?.kind === 'report-setup' && lead?.item.eligibility?.reasonCode === 'stage-locked'
    ? SCENARIO_ROUTE
    : leadAction;
  const scenario: StageScenarioStep = {
    needed: scenarioItem != null,
    afterScreening: scenarioItem != null && !screeningComplete,
    action: scenarioAction,
    dependants,
    stale: gated.some((row) => row.item.eligibility?.reasonCode === 'generation-scenario-stale'),
  };
  const validationRequired = requiredOf(validationRows);
  const validationBase = boardState(validationRequired);
  const validation: StageBoardModel = {
    stage: 2,
    label: DECISION_STAGE_LABEL[2],
    rows: validationRows,
    requiredTotal: validationRequired.length,
    requiredUsed: validationRequired.filter((row) => row.used).length,
    requiredCurrent: validationRequired.filter((row) => row.current).length,
    requiredSaved: validationRequired.filter((row) => row.hasRecord).length,
    state: scenario.needed && validationBase !== 'complete'
      ? (scenario.afterScreening ? 'after-screening' : 'scenario')
      : validationBase,
    scenario,
  };

  const runNow = rows.filter((row) => row.startable);
  const inFlight = rows.filter((row) => row.running && row.lane === 'run-now');
  const current = rows.filter((row) => row.current);
  const later = rows.filter((row) => row.lane === 'available-later');

  // The next step, in pipeline order. A startable Stage 1 row leads; when
  // screening is complete and Stage 2 waits for a scenario, the scenario step
  // leads; then Stage 2 work; then whatever is running; then the decision.
  // REQUIRED work leads; an intent-relevant analysis is useful work the
  // decision does not wait for, so it comes after the decision itself.
  const requiredRunNow = runNow.filter((row) => row.item.role === 'required' && !row.used);
  const startable = (stage: DecisionStage) => requiredRunNow.find((row) => row.stage === stage);
  const startableUnstaged = requiredRunNow.find((row) => row.stage == null);
  const requiredStaged = requiredOf(staged);
  let next: StageNextStep;
  const first1 = startable(1) ?? startableUnstaged;
  if (first1) next = { kind: 'analysis', row: first1, action: first1.item.primaryAction ?? null };
  else if (screeningComplete && scenario.needed) next = { kind: 'scenario', action: scenario.action };
  else if (startable(2)) { const row = startable(2)!; next = { kind: 'analysis', row, action: row.item.primaryAction ?? null }; }
  else if (inFlight.some((row) => row.item.role === 'required' && !row.used)) next = { kind: 'running', row: inFlight.find((row) => row.item.role === 'required' && !row.used)! };
  else if (requiredStaged.length > 0 && requiredStaged.every((row) => row.used)) next = { kind: 'decision' };
  else if (runNow.length > 0) next = { kind: 'analysis', row: runNow[0], action: runNow[0].item.primaryAction ?? null };
  else if (inFlight.length > 0) next = { kind: 'running', row: inFlight[0] };
  else next = { kind: 'none' };

  const work = countStartableWork(runNow.map((row) => ({ lifecycle: row.item.lifecycle, hasRecord: row.hasRecord })));
  return {
    screening, validation, optional, runNow, inFlight, current, later, next,
    toRefreshCount: work.toRefresh,
    toRunCount: work.toRun,
    needsAttentionCount: work.needsAttention,
  };
}

/** Startable work counted by the verb of each row's own action. */
export interface StartableWorkCounts {
  toRefresh: number;
  toRun: number;
  needsAttention: number;
}

export type StartableWorkKind = 'refresh' | 'run' | 'attention';

/**
 * The verb a startable row's count uses — the verb of its own action, so a
 * count never names work the row's button does not do (2026-09-18):
 *   - 'refresh-needed' → refresh ("Refresh analysis"; always a saved row).
 *   - 'ready', or no saved record → run (never run; a failed first attempt's
 *     retry is still a first run, as the count said before the split).
 *   - any other saved row → attention: a quality hold, a failed or
 *     coverage-blocked rerun, a preliminary screening or a preview awaits a
 *     review, retry or written report — neither "run" (it has run) nor
 *     "refresh" (its button says otherwise).
 */
export function startableWorkKind(row: { lifecycle: string; hasRecord: boolean }): StartableWorkKind {
  if (row.lifecycle === 'refresh-needed') return 'refresh';
  if (row.lifecycle === 'ready' || !row.hasRecord) return 'run';
  return 'attention';
}

export function countStartableWork(rows: readonly { lifecycle: string; hasRecord: boolean }[]): StartableWorkCounts {
  const counts: StartableWorkCounts = { toRefresh: 0, toRun: 0, needsAttention: 0 };
  for (const row of rows) {
    const kind = startableWorkKind(row);
    if (kind === 'refresh') counts.toRefresh += 1;
    else if (kind === 'run') counts.toRun += 1;
    else counts.needsAttention += 1;
  }
  return counts;
}

/**
 * Stamp the Decision Guide's screening result on the Stage 1 board. The plan
 * knows presence and currency; the rollup knows the verdict tiers.
 */
export function withScreeningOutcome(model: StageBoardsModel, stages: DecisionStagesRollup | null | undefined): StageBoardsModel {
  const screening = stages?.screening;
  if (!screening || (!screening.outcome && screening.score == null)) return model;
  // Numeric coverage is populated pillar weight, not publication authority.
  // A held/stale/preview report may leave a useful score but cannot complete screening.
  const used = model.screening.requiredTotal > 0
    && model.screening.state === 'complete'
    && screening.complete
    && model.screening.rows.filter(row => row.item.role === 'required')
      .every(row => (
        row.item.lifecycle !== 'quality-held'
        && row.item.lifecycle !== 'preview'
        && screening.qualifiedTypes?.includes(row.type) === true
      ));
  const current = used
    && model.screening.requiredCurrent === model.screening.requiredTotal;
  return {
    ...model,
    screening: {
      ...model.screening,
      outcome: used ? screening.outcome : undefined,
      concernTypes: used && screening.outcome ? screening.concernTypes ?? [] : undefined,
      ...(screening.score != null ? {
        score: screening.score,
        scoreCoverage: screening.scoreCoverage ?? 0,
        scoreBasis: current ? 'current' as const : used ? 'saved' as const : 'provisional' as const,
      } : {}),
    },
  };
}

/** Describes score provenance without changing score arithmetic or authority gates. */
export type ScreeningScoreBasis = 'current' | 'provisional' | 'saved';

export function screeningScoreLabel(score: number, coverage?: number, basis: ScreeningScoreBasis = 'current'): string {
  const prefix = basis === 'provisional' ? 'Provisional screening' : basis === 'saved' ? 'Saved screening' : 'Screening';
  const cov = coverage != null ? ` · ${Math.round(coverage * 100)}% of screening weight has data` : '';
  return `${prefix} score ${score} / 100${cov}`;
}

/** Short subline; coverage describes weighted inputs, not completed analysis or measurement quality. */
export function screeningScoreShort(score: number, coverage?: number, basis: ScreeningScoreBasis = 'current'): string {
  const prefix = basis === 'provisional' ? 'Provisional score' : basis === 'saved' ? 'Saved score' : 'score';
  const cov = coverage != null ? ` · ${Math.round(coverage * 100)}% weight with data` : '';
  return `${prefix} ${score} / 100${cov}`;
}

/** Human label for a screening result — its own vocabulary, never a band. */
export function screeningOutcomeLabel(outcome: ScreeningOutcome, concernTypes: readonly ReportTypeId[] = []): string {
  if (outcome === 'clear') return 'Screening clear';
  if (outcome === 'declined') return 'Screening declined';
  const names = concernTypes.map((type) => REPORT_TYPE_REGISTRY[type]?.shortLabel ?? type);
  return names.length > 0 ? `Screening with concerns — ${names.join(', ')}` : 'Screening with concerns';
}

/**
 * The " · K saved" tail of a stage count ("0 of 3 current · 3 saved").
 *
 * Shown only when saved records OUTNUMBER current ones (K > N): that is the
 * case the bare count misreads, where analyses exist but none of them counts
 * as current. Omitted when K == N (every saved record is already counted as
 * current — "3 of 3 current · 3 saved" says nothing new) and when K == 0.
 * A current row always carries a record, so K < N does not arise from the
 * plan; it is omitted too rather than printing a smaller "saved" than
 * "current".
 */
export function stageSavedSuffix(board: Pick<StageBoardModel, 'requiredCurrent' | 'requiredSaved'>): string {
  return board.requiredSaved > board.requiredCurrent ? ` · ${board.requiredSaved} saved` : '';
}

/**
 * "N to refresh · M to run · K need attention" for startable work, naming
 * only the non-zero parts; '' when there is nothing startable. A saved
 * analysis that is not current is never "to run" — that read as never run.
 * With `noun`, a count of ONE kind reads "3 analyses to refresh" (Path to
 * GO › Analyse); mixed kinds stay terse on every surface.
 */
export function startableWorkLabel(counts: StartableWorkCounts, options: { noun?: boolean } = {}): string {
  const parts: Array<[number, string, string]> = [
    [counts.toRefresh, 'to refresh', 'to refresh'],
    [counts.toRun, 'to run', 'to run'],
    [counts.needsAttention, 'needs attention', 'need attention'],
  ];
  const present = parts.filter(([count]) => count > 0);
  if (options.noun && present.length === 1) {
    const [count, singular, plural] = present[0]!;
    return count === 1 ? `1 analysis ${singular}` : `${count} analyses ${plural}`;
  }
  return present
    .map(([count, singular, plural]) => `${count} ${count === 1 ? singular : plural}`)
    .join(' · ');
}

/** The strip's spoken count separates safe calculation use from currency. */
export function stageCountLabel(board: StageBoardModel): string {
  switch (board.state) {
    case 'nothing-required': return 'nothing required';
    case 'scenario': return 'scenario first';
    case 'after-screening': return 'after screening';
    default: return board.requiredUsed === board.requiredTotal
      && board.requiredCurrent === board.requiredTotal
      ? `${board.requiredCurrent} of ${board.requiredTotal} current`
      : `${board.requiredUsed} of ${board.requiredTotal} used · ${board.requiredCurrent} current`;
  }
}
